import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Plus, Edit, Trash2, ArrowLeft, Filter, FolderPlus, Calendar, Eye, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import PortalLayout from '../../../components/PortalLayout';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import axios from 'axios';

const BlogManagement = () => {
  const { token } = useAuth();
  const toast = useToast();
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAuthor, setFilterAuthor] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(10); // Changed to 10, now user-adjustable
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, postId: null, postTitle: '' });
  const [editingDate, setEditingDate] = useState(null);
  const [authors, setAuthors] = useState([]);
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    extractAuthors();
  }, []);

  useEffect(() => {
    filterAndSortPosts();
  }, [searchTerm, filterCategory, filterStatus, filterAuthor, dateRange, sortBy, sortOrder, allPosts]);

  const extractAuthors = () => {
    const uniqueAuthors = [...new Set(allPosts.map(post => post.author).filter(Boolean))];
    setAuthors(uniqueAuthors);
  };

  useEffect(() => {
    extractAuthors();
  }, [allPosts]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/admin/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllPosts(response.data.posts);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filterAndSortPosts = () => {
    let filtered = [...allPosts];

    // Apply filters
    if (searchTerm) {
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.excerpt.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterCategory && filterCategory !== 'all') {
      filtered = filtered.filter(post => post.category === filterCategory);
    }

    if (filterStatus && filterStatus !== 'all') {
      filtered = filtered.filter(post => post.status === filterStatus);
    }

    if (filterAuthor && filterAuthor !== 'all') {
      filtered = filtered.filter(post => post.author === filterAuthor);
    }

    if (dateRange.start) {
      filtered = filtered.filter(post => {
        const postDate = new Date(post.published_at || post.created_at);
        return postDate >= new Date(dateRange.start);
      });
    }

    if (dateRange.end) {
      filtered = filtered.filter(post => {
        const postDate = new Date(post.published_at || post.created_at);
        return postDate <= new Date(dateRange.end);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let compareA, compareB;

      switch (sortBy) {
        case 'title':
          compareA = a.title.toLowerCase();
          compareB = b.title.toLowerCase();
          break;
        case 'author':
          compareA = (a.author || '').toLowerCase();
          compareB = (b.author || '').toLowerCase();
          break;
        case 'category':
          compareA = (a.category || '').toLowerCase();
          compareB = (b.category || '').toLowerCase();
          break;
        case 'date':
        default:
          compareA = new Date(a.created_at || a.published_at);
          compareB = new Date(b.created_at || b.published_at);
      }

      if (sortOrder === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });

    setPosts(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const deletePost = async (postId, postTitle) => {
    setDeleteConfirm({ isOpen: true, postId, postTitle });
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/admin/posts/${deleteConfirm.postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPosts();
      toast.success('Blog post deleted successfully');
    } catch (error) {
      toast.error('Error deleting post');
    }
  };

  const togglePublishStatus = async (postId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'published' ? 'draft' : 'published';
      await axios.put(
        `${BACKEND_URL}/api/blog/admin/posts/${postId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      fetchPosts();
      toast.success(`Post ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`);
    } catch (error) {
      toast.error('Error updating post status');
    }
  };

  const viewOnSite = (slug) => {
    const siteUrl = window.location.origin;
    window.open(`${siteUrl}/blog/${slug}`, '_blank');
  };

  const updatePostDate = async (postId, newDate) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/blog/admin/posts/${postId}/date`, 
        { published_at: newDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingDate(null);
      fetchPosts();
      toast.success('Post date updated successfully');
    } catch (error) {
      toast.error('Error updating post date');
    }
  };

  // Pagination logic
  const filteredPosts = posts;
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      await axios.post(`${BACKEND_URL}/api/blog/admin/categories`, 
        { name: newCategoryName, description: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewCategoryName('');
      setShowCategoryModal(false);
      fetchCategories();
    } catch (error) {
      alert('Error creating category');
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/blog/admin/categories/${catId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchCategories();
    } catch (error) {
      alert('Error deleting category');
    }
  };

  return (
    <PortalLayout>
      <div className="p-8">
        {/* Compact Header with Filters and Buttons */}
        <div className="mb-8">
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Blog Management</h1>
            <p className="text-sm text-gray-600">Create and manage blog posts</p>
          </div>

          {/* Compact Single-Row: Buttons + Filters */}
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <div className="flex flex-wrap gap-3 items-center">
              {/* Action Buttons - Left Aligned */}
              <Button onClick={() => navigate('/portal/admin/blog/create')} className="bg-blue-600 hover:bg-blue-700 px-5 h-9">
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
              <Button variant="outline" onClick={() => setShowCategoryModal(true)} className="border-green-600 text-green-600 hover:bg-green-50 h-9">
                <FolderPlus className="w-4 h-4 mr-2" />
                Categories
              </Button>

              <div className="w-px h-8 bg-gray-300 mx-2" />

              {/* Search & Filters */}
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="w-36">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (<SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <Select value={filterAuthor} onValueChange={setFilterAuthor}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Author" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Authors</SelectItem>
                    {authors.map(author => (<SelectItem key={author} value={author}>{author}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-px h-8 bg-gray-300 mx-2" />

              {/* Sort & Display Controls */}
              <div className="w-32">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">By Date</SelectItem>
                    <SelectItem value="title">By Title</SelectItem>
                    <SelectItem value="author">By Author</SelectItem>
                    <SelectItem value="category">By Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Newest</SelectItem>
                    <SelectItem value="asc">Oldest</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Select value={postsPerPage.toString()} onValueChange={(val) => setPostsPerPage(parseInt(val))}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 / page</SelectItem>
                    <SelectItem value="25">25 / page</SelectItem>
                    <SelectItem value="50">50 / page</SelectItem>
                    <SelectItem value="100">100 / page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="ml-auto text-sm text-gray-600 font-medium whitespace-nowrap">
                {posts.length > 0 ? `${indexOfFirstPost + 1}-${Math.min(indexOfLastPost, posts.length)} of ${posts.length}` : '0 posts'}
              </div>
            </div>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="grid gap-4">
          {currentPosts.map((post) => (
            <motion.div key={post.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-all">
              <div className="flex">
                <img src={post.featured_image && post.featured_image.startsWith('http') ? post.featured_image : post.featured_image ? `${BACKEND_URL}${post.featured_image}` : '/placeholder-blog.jpg'} alt={post.title} className="w-48 h-32 object-cover" loading="lazy" />
                <div className="flex-1 p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-1">{post.title}</h3>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center space-x-3 text-xs text-gray-500">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">{post.category}</span>
                        <span>•</span>
                        <span>{post.author}</span>
                        <span>•</span>
                        {editingDate === post.id ? (
                          <div className="flex items-center gap-2">
                            <input 
                              type="date" 
                              defaultValue={post.published_at ? new Date(post.published_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]}
                              className="px-2 py-1 border border-blue-300 rounded text-xs"
                              onBlur={(e) => {
                                if (e.target.value) {
                                  updatePostDate(post.id, e.target.value);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  updatePostDate(post.id, e.target.value);
                                } else if (e.key === 'Escape') {
                                  setEditingDate(null);
                                }
                              }}
                              autoFocus
                            />
                          </div>
                        ) : (
                          <button 
                            onClick={() => setEditingDate(post.id)}
                            className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                          >
                            <Calendar className="w-3 h-3" />
                            {post.published_at ? new Date(post.published_at).toLocaleDateString() : 'Set date'}
                          </button>
                        )}
                        <span>•</span>
                        <span className={`px-2 py-1 rounded ${post.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{post.status}</span>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <Button 
                        onClick={() => viewOnSite(post.slug)} 
                        size="sm" 
                        variant="outline"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button 
                        onClick={() => togglePublishStatus(post.id, post.status)} 
                        size="sm" 
                        variant="outline"
                        className={post.status === 'published' ? 'border-yellow-300 text-yellow-600 hover:bg-yellow-50' : 'border-green-300 text-green-600 hover:bg-green-50'}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-1" />
                        {post.status === 'published' ? 'Unpublish' : 'Publish'}
                      </Button>
                      <Button 
                        onClick={() => navigate(`/portal/admin/blog/edit/${post.id}`)} 
                        size="sm" 
                        variant="outline"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        onClick={() => deletePost(post.id, post.title)} 
                        size="sm" 
                        variant="outline" 
                        className="border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-4">
            <div className="text-sm text-gray-600">
              Showing {indexOfFirstPost + 1}-{Math.min(indexOfLastPost, posts.length)} of {posts.length} posts
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="disabled:opacity-50"
              >
                Previous
              </Button>
              
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 rounded ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } transition-colors text-sm`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <Button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="disabled:opacity-50"
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Category Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-2xl w-full max-h-[80vh] overflow-auto">
              <h2 className="text-2xl font-bold mb-6">Manage Categories</h2>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">New Category</label>
                <div className="flex gap-2">
                  <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder="Enter category name" className="flex-1 px-4 py-2 border rounded-lg" />
                  <Button onClick={createCategory} className="bg-green-600">Add</Button>
                </div>
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-sm text-gray-700 mb-3">Existing Categories ({categories.length})</h3>
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{cat.name}</span>
                    <button onClick={() => deleteCategory(cat.id)} className="text-red-600 hover:text-red-700 text-sm">Delete</button>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <Button onClick={() => setShowCategoryModal(false)} variant="outline" className="w-full">Close</Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, postId: null, postTitle: '' })}
          onConfirm={confirmDelete}
          title="Delete Blog Post?"
          message={`Are you sure you want to delete this blog post?\n\n"${deleteConfirm.postTitle}"\n\nThis action cannot be undone.`}
          confirmText="Delete Post"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </PortalLayout>
  );
};

export default BlogManagement;
